import {memo} from 'react'
import FormErrorMessage from '../FormErrorMessage/FormErrorMessage'
import {daysOfWeek} from '../../types'
import {FetchVenuesResponse as Address} from '../../services'
import {kegstarCollectionEmailAddress} from '../../utils/customerSupportEmailAddress'

type DayOfWeek = keyof typeof daysOfWeek

interface AvailableDaysRadioCardsProps {
  handleOnClick: (day: DayOfWeek) => void
  collectionDays?: DayOfWeek[]
  flagFormErrors?: boolean
  isDisabled?: boolean
  chosenLocation?: Address | null
}

const AvailableDaysRadioCards = ({
  handleOnClick,
  collectionDays = [],
  flagFormErrors,
  isDisabled = false,
  chosenLocation = null,
}: AvailableDaysRadioCardsProps) => {
  const noDaysSelected = collectionDays.length === 0

  const emailLinkMarkup = (
    <a
      href={`mailto:${kegstarCollectionEmailAddress(chosenLocation, null)}?subject=${encodeURIComponent('Keg Collection Request - Special Collection Requirements')}`}
      target="_blank"
      rel="noopener noreferrer"
      className="text-sky-500 dark:text-sky-400"
    >
      {kegstarCollectionEmailAddress(chosenLocation, null)}
    </a>
  )
  const uniqueCollectionRequirementsHelperText = (
    <p className="text-xs mt-2 font-bold text-slate-500 dark:text-slate-400">
      {`Please note: We do not offer specific day collections, please only select the days the site is open and closed. If
      the collection requires unique requirements, please reach out at `}
      {emailLinkMarkup}
      {`.`}
    </p>
  )

  return (
    <div className="mb-5">
      <label
        className={`block text-xs ${flagFormErrors && noDaysSelected ? 'text-red-600 dark:text-red-300' : 'text-sky-500'}`}
      >
        Available Collection Days
        <span className="text-red-600 pl-1">*</span>
      </label>
      <p className={`text-xs font-semibold text-slate-500 dark:text-slate-400`}>
        We aim to collect within 5 - 7 working days of booking.
      </p>
      <div
        className="flex gap-2 my-2"
        role="group"
        aria-label="Available Collection Days"
      >
        {Object.entries(daysOfWeek).map(([day, abbreviation]) => (
          <button
            type="button"
            className={[
              'w-1/5 py-2 text-center border rounded-sm',
              flagFormErrors && noDaysSelected
                ? 'border-red-300'
                : 'border-gray-300 dark:border-slate-600 focus:border-sky-500',
              'focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-1 dark:focus:ring-offset-slate-900',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              collectionDays.includes(day as DayOfWeek)
                ? 'bg-sky-500 dark:bg-sky-600 text-white'
                : 'bg-transparent text-black dark:text-slate-200',
            ].join(' ')}
            onClick={() => handleOnClick(day as DayOfWeek)}
            disabled={isDisabled}
            aria-pressed={collectionDays.includes(day as DayOfWeek)}
            key={day}
          >
            {abbreviation}
          </button>
        ))}
      </div>
      {flagFormErrors && noDaysSelected ? (
        <FormErrorMessage message="Must select at least one collection day." />
      ) : (
        uniqueCollectionRequirementsHelperText
      )}
    </div>
  )
}

export default memo(AvailableDaysRadioCards)
